'use client'

import { useStoreI18n } from '@/store'
import { Typo3SectionSpotSanityTour } from '@/types'
import { Container, Skeleton } from '@/ui/components'
import { OUTDOORACTIVE } from '@spot'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

declare global {
  interface Window {
    oa?: {
      api: {
        detailpage: (config: { id: string; actionOpenType: string }) => {
          whenLoaded: (cb: () => void) => void
        }
      }
    }
  }
}

const MAX_ATTEMPTS = 30
const POLL_INTERVAL = 200

// Loads the OutdoorActive bootstrap on-demand. Skips oa_head.js (which uses
// document.write to redirect to oax/oam_head.js) and goes directly to the
// right variant. Both oax_head.js and oam_head.js *also* use document.write
// internally to load oax/api.js + inline config; calling document.write after
// initial parse wipes the page, so we patch it temporarily, capture the
// writes, then re-inject them as real <script>/<link> elements appended to
// <head> in order.
const ensureOaScript = (lang: string) => {
  if (typeof window === 'undefined') {
    return
  }
  if (!OUTDOORACTIVE?.proj || !OUTDOORACTIVE?.key) {
    return
  }
  if (window.oa?.api) {
    return
  }
  const isMobile = /\b(iPhone|iPad|Android|mobile)\b/i.test(navigator.userAgent)
  const file = isMobile ? 'oam_head.js' : 'oax_head.js'
  if (document.querySelector(`script[data-oa-bootstrap="${file}"]`)) {
    return
  }

  const captured: string[] = []
  const origWrite = document.write
  const origWriteln = document.writeln
  document.write = function patchedWrite(
    this: Document,
    ...args: string[]
  ): void {
    captured.push(args.join(''))
  } as Document['write']
  document.writeln = function patchedWriteln(
    this: Document,
    ...args: string[]
  ): void {
    captured.push(args.join('') + '\n')
  } as Document['writeln']

  const restore = () => {
    document.write = origWrite
    document.writeln = origWriteln
  }

  const replay = () => {
    if (captured.length === 0) {
      return
    }
    const tmp = document.createElement('div')
    tmp.innerHTML = captured.join('')
    const elements = Array.from(tmp.children)
    let idx = 0
    const next = () => {
      if (idx >= elements.length) {
        return
      }
      const el = elements[idx++]
      if (el.tagName === 'SCRIPT') {
        const old = el as HTMLScriptElement
        const s = document.createElement('script')
        if (old.type) {
          s.type = old.type
        }
        if (old.src) {
          s.src = old.src
          s.async = false
          s.onload = next
          s.onerror = next
          document.head.appendChild(s)
        } else {
          s.textContent = old.textContent || ''
          document.head.appendChild(s)
          next()
        }
      } else if (el.tagName === 'LINK' || el.tagName === 'STYLE') {
        document.head.appendChild(el.cloneNode(true))
        next()
      } else {
        next()
      }
    }
    next()
  }

  const script = document.createElement('script')
  script.src = `https://api-oa.com/jscr/${file}?proj=${OUTDOORACTIVE.proj}&key=${OUTDOORACTIVE.key}&lang=${lang}`
  script.async = false
  script.dataset.oaBootstrap = file
  script.onload = () => {
    restore()
    replay()
  }
  script.onerror = restore
  document.head.appendChild(script)
}

const Detail = ({ data }: { data: Typo3SectionSpotSanityTour }) => {
  const baseTourId = data?.content?.item?.tourId
  const backHref =
    data?.content?.uriBack?.href || data?.content?.uriList?.href || ''
  const currentLocale = useStoreI18n.use.currentLocale()
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(true)
  const unmountedRef = useRef(false)

  useEffect(() => {
    unmountedRef.current = false

    const tourId = baseTourId?.toString() ?? ''

    if (!tourId) {
      return
    }

    ensureOaScript(currentLocale)

    const wireBackButton = () => {
      if (!backHref) {
        return
      }
      const anchors = document.querySelectorAll<HTMLAnchorElement>(
        '.oax-backlink-detailpage'
      )
      anchors.forEach((a) => {
        if (a.dataset.surselvaBack === '1') {
          return
        }
        a.dataset.surselvaBack = '1'
        a.setAttribute('href', backHref)
        // OA hides the back button when the user arrived directly (no in-app
        // history). We always have a configured list URL, so force-show it.
        a.classList.remove('oax_no_display')
        a.parentElement?.classList.remove('oax_no_display')
        a.addEventListener('click', (e) => {
          e.preventDefault()
          router.push(backHref)
        })
      })
    }

    const waitForOaApi = (attempts = 0): void => {
      if (unmountedRef.current) {
        return
      }
      if (window.oa?.api) {
        const conf = {
          id: tourId,
          actionOpenType: 'in-page'
        }

        // instantiate FlexView API detail page
        const dp = window.oa?.api?.detailpage?.(conf)

        dp.whenLoaded(() => {
          if (!unmountedRef.current) {
            setIsLoading(false)
            wireBackButton()
          }
        })
      } else if (attempts < MAX_ATTEMPTS) {
        setTimeout(() => waitForOaApi(attempts + 1), POLL_INTERVAL)
      } else {
        if (!unmountedRef.current) {
          setIsLoading(false)
        }
      }
    }

    waitForOaApi()

    return () => {
      unmountedRef.current = true
    }
  }, [baseTourId, currentLocale, backHref, router])

  if (!baseTourId) {
    return null
  }

  return (
    <Container className="mt-16">
      <div className="relative">
        {isLoading && (
          <div className="gap-sm relative inset-0 bottom-auto z-10 flex flex-col md:grid md:grid-cols-3">
            <Skeleton className="mt-8 h-10 w-full bg-neutral-200 md:col-span-3" />
            <div className="gap-sm col-span-2 flex flex-col">
              <Skeleton className="h-12 w-full bg-neutral-200" />
              <Skeleton className="h-16 w-full bg-neutral-200" />
            </div>
            <div>
              <Skeleton className="h-full w-full bg-neutral-200" />
            </div>
            <Skeleton className="col-span-2 aspect-16/8 w-full bg-neutral-200" />
            <Skeleton className="h-full w-full bg-neutral-200" />
            <Skeleton className="col-span-2 aspect-16/8 w-full bg-neutral-200" />
            <Skeleton className="h-full w-full bg-neutral-200" />
          </div>
        )}
        <style>
          {`
            .oax.alp-x-lightbox-cont {
              z-index: 9999900 !important;
            }
            .oax .oax_singleView {
             z-index: 1999900 !important;
            }
          `}
        </style>
        <div className="oax-top-cont">
          <div className="oax-part-singleviewpage" />
        </div>
      </div>
    </Container>
  )
}

export default Detail
